import type { GpsSample } from '../sensors/types';
import type { UploadEvent } from '../sensors/types';

/**
 * Home-radius privacy guard.
 *
 * Trims the start and end of every trip in geographic space, so the
 * raw uploads never include events near the user's origin or final
 * resting position. Defends against home/work inference from cluster
 * centroids in the public aggregate.
 *
 * Model:
 *
 *   - The FIRST GPS fix after Start is the "origin". Any event whose
 *     lat/lon is within `radiusM` of the origin is dropped immediately
 *     (origin trim).
 *
 *   - Other events go into a pending buffer. They stay there until a
 *     subsequent GPS fix proves the device has moved `radiusM` past
 *     them. At that point they're released to the upload queue.
 *
 *   - On trip stop, the pending buffer is discarded. Anything still in
 *     it never moved `radiusM` away from the device — i.e. it's near
 *     where the trip ended. End trim.
 *
 * The trim is enforced client-side BEFORE enqueue. By the time data
 * reaches the upload queue, the spatial filter has already run, so
 * the wire payload contains no near-home events at all. A backend
 * defence is not possible without the server knowing the user's home,
 * which is precisely what we're trying to avoid.
 *
 * Events with null lat/lon (no GPS fix at detection time) are dropped
 * outright — without a position we can't enforce the guarantee.
 */

const EARTH_RADIUS_M = 6_371_000;

function distanceM(
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number,
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(x));
}

export interface HomeRadiusGuardOpts {
  /** Trim radius in metres. Default 75. */
  radiusM?: number;
}

export interface GuardCounters {
  /** Events discarded because they fell within `radiusM` of origin. */
  droppedOrigin: number;
  /** Events discarded because they had no lat/lon. */
  droppedNoFix: number;
  /** Events discarded on reset() — they never moved `radiusM` from the trip end. */
  droppedEndTrim: number;
  /** Events that passed the guard and were released to the queue. */
  released: number;
  /** Currently held in the pending buffer. */
  pending: number;
}

export class HomeRadiusGuard {
  private readonly radiusM: number;

  private originLat: number | null = null;
  private originLon: number | null = null;
  private latestLat: number | null = null;
  private latestLon: number | null = null;
  private buffer: UploadEvent[] = [];

  private counters: GuardCounters = {
    droppedOrigin: 0,
    droppedNoFix: 0,
    droppedEndTrim: 0,
    released: 0,
    pending: 0,
  };

  constructor(opts: HomeRadiusGuardOpts = {}) {
    this.radiusM = opts.radiusM ?? 75;
  }

  /**
   * Update current GPS position. First call also sets the origin.
   * Returns events that the new position has cleared for release.
   */
  updateGps(s: GpsSample): UploadEvent[] {
    if (this.originLat === null) {
      this.originLat = s.lat;
      this.originLon = s.lon;
    }
    this.latestLat = s.lat;
    this.latestLon = s.lon;
    return this.evictReleasable();
  }

  /**
   * Submit a detected event. Returns events that are immediately
   * releasable — usually empty (the just-submitted event almost
   * always goes into the buffer), but a previously-buffered event
   * could also clear if the new event's GPS position carried us past
   * it. Callers should enqueue the returned events.
   */
  submit(e: UploadEvent): UploadEvent[] {
    if (e.lat == null || e.lon == null) {
      this.counters.droppedNoFix += 1;
      return [];
    }
    if (
      this.originLat !== null &&
      this.originLon !== null &&
      distanceM(e.lat, e.lon, this.originLat, this.originLon) < this.radiusM
    ) {
      this.counters.droppedOrigin += 1;
      return [];
    }
    this.buffer.push(e);
    this.counters.pending = this.buffer.length;
    return this.evictReleasable();
  }

  /**
   * Called on trip stop. Discards the pending buffer (these events
   * never proved they'd left the trip-end vicinity) and clears state
   * so the next trip starts with a fresh origin.
   */
  reset(): { droppedEndTrim: number } {
    const dropped = this.buffer.length;
    this.counters.droppedEndTrim += dropped;
    this.buffer = [];
    this.counters.pending = 0;
    this.originLat = null;
    this.originLon = null;
    this.latestLat = null;
    this.latestLon = null;
    return { droppedEndTrim: dropped };
  }

  /** Snapshot of counters for the on-page diagnostics readout. */
  stats(): GuardCounters {
    return { ...this.counters, pending: this.buffer.length };
  }

  private evictReleasable(): UploadEvent[] {
    if (this.latestLat === null || this.latestLon === null) {
      return [];
    }
    const released: UploadEvent[] = [];
    const kept: UploadEvent[] = [];
    for (const ev of this.buffer) {
      const d = distanceM(ev.lat, ev.lon, this.latestLat, this.latestLon);
      if (d >= this.radiusM) {
        released.push(ev);
      } else {
        kept.push(ev);
      }
    }
    this.buffer = kept;
    this.counters.pending = kept.length;
    this.counters.released += released.length;
    return released;
  }
}
